import { useState } from "react";
import { invokeAdmin } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner, CopyId } from "../components/ui";

type ContentItem = {
  id: string;
  entity_kind: string | null;
  entity_id: string | null;
  author_id: string | null;
  kind: string;
  body: string | null;
  image_url: string | null;
  verdict: string;
  reason: string | null;
  created_at: string;
};

const VERDICTS = ["blocked", "allowed", "pending"] as const;
type Verdict = (typeof VERDICTS)[number];

const KINDS = ["post", "comment", "review", "photo", "username"] as const;

function verdictBadgeClass(verdict: string): string {
  switch (verdict) {
    case "blocked":
      return "bg-red-950 text-red-300 border border-red-900";
    case "allowed":
      return "bg-green-950 text-green-300 border border-green-900";
    default:
      return "bg-zinc-800 text-zinc-400 border border-zinc-700";
  }
}

function truncate(text: string, max = 160): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export default function ContentModerationPage() {
  const [verdict, setVerdict] = useState<Verdict>("blocked");
  const [kind, setKind] = useState("");

  // Cancellation-safe fetch keyed on the filters (ADM-5): rapid verdict/kind
  // toggling can no longer let a slow earlier response clobber the newer one.
  const { data, loading, error } = useAdminQuery(
    () => {
      const body: Record<string, unknown> = { verdict, limit: 50 };
      if (kind) body.kind = kind;
      return invokeAdmin<{ content: ContentItem[]; total?: number }>(
        "admin_moderation_content_list",
        body,
      );
    },
    [verdict, kind],
  );
  const content = data?.content ?? [];
  const total = data?.total ?? null;

  return (
    <div>
      <h1 className="text-xl font-semibold">Moderation</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Read-only review of the AI-moderation log — what the gate allowed, blocked,
        or left pending.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
          {VERDICTS.map((v) => (
            <button
              key={v}
              onClick={() => setVerdict(v)}
              className={`rounded px-3 py-1 text-sm capitalize ${
                verdict === v
                  ? "bg-zinc-700 text-white"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-zinc-500"
        >
          <option value="">All kinds</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>

        {total !== null && (
          <span className="ml-auto text-xs text-zinc-500">{total} total</span>
        )}
      </div>

      {error && <Banner className="mt-3">{error}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Kind</th>
              <th className="px-3 py-2 font-medium">Verdict</th>
              <th className="px-3 py-2 font-medium">Entity</th>
              <th className="px-3 py-2 font-medium">Author</th>
              <th className="px-3 py-2 font-medium">Content</th>
              <th className="px-3 py-2 font-medium">Reason</th>
              <th className="px-3 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {content.map((c) => (
              <tr key={c.id} className="border-t border-zinc-800 align-top">
                <td className="px-3 py-2 text-zinc-300">{c.kind}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs capitalize ${verdictBadgeClass(
                      c.verdict,
                    )}`}
                  >
                    {c.verdict}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {c.entity_kind ?? "—"}
                  {c.entity_id && (
                    <span className="block">
                      <CopyId id={c.entity_id} />
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <CopyId id={c.author_id} />
                </td>
                <td className="px-3 py-2 text-zinc-300 max-w-md">
                  {c.body && <p className="whitespace-pre-wrap break-words">{truncate(c.body)}</p>}
                  {c.image_url && (
                    <img
                      src={c.image_url}
                      alt=""
                      className="mt-1 h-16 w-16 rounded object-cover border border-zinc-700"
                    />
                  )}
                  {!c.body && !c.image_url && <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs max-w-xs">
                  {c.reason ?? "—"}
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs whitespace-nowrap">
                  {new Date(c.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
            {content.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-sm text-zinc-500">
                  No content found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-sm text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
